import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import NavbarHome from '../../components/NavbarHome';
import VihsMark from '../../components/VihsMark';
import { useProjectRequest } from '../../contexts/ProjectRequestContext';
import './Home.css';

const CONTACT_URL = 'https://discord.gg/ScdKWjtkVd';

const services = [
  {
    name: 'Sistemas sob medida',
    detail: 'Painéis, automações e plataformas pensadas para o fluxo do seu negócio.',
  },
  {
    name: 'Websites',
    detail: 'Presença digital clara, rápida e alinhada à identidade da marca.',
  },
  {
    name: 'Bots Discord',
    detail: 'Moderação, utilidades e experiências customizadas para comunidades.',
  },
  {
    name: 'Design',
    detail: 'Identidade visual, interfaces e materiais que comunicam com precisão.',
  },
  {
    name: 'Infraestrutura',
    detail: 'Arquitetura, performance e operação estável para servidores.',
  },
];

const ecosystem = [
  {
    name: "Vih's Studio",
    role: 'Braço comercial',
    description:
      'Estúdio de criação e entrega de soluções digitais sob demanda — o ponto de contato para contratar serviços.',
    primary: true,
    href: '/studio',
    cta: 'Conhecer o Studio',
  },
  {
    name: "Vih's Bot",
    role: 'Interno',
    description:
      'Bot interno da Vih\'s no Discord — apoia a marca na operação e no atendimento. A criação de bots para outros servidores é um serviço do Studio.',
    href: CONTACT_URL,
    cta: 'Ver no Discord',
    external: true,
  },
  {
    name: "Vih's Dev Hub",
    role: 'Produto',
    description:
      'Espaço para desenvolvedores e criadores — recursos, comunidade e infraestrutura digital.',
    href: '/dev-hub',
    cta: 'Conhecer o Dev Hub',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
};

export default function Home() {
  const { openProjectRequest } = useProjectRequest();

  return (
    <div className="vihs-home">
      <NavbarHome />

      <section className="vihs-hero" aria-label="Apresentação">
        <div className="vihs-hero-atmosphere" aria-hidden="true">
          <div className="vihs-hero-glow" />
          <div className="vihs-hero-mesh" />
        </div>

        <div className="vihs-hero-inner">
          <motion.p
            className="vihs-brand"
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            Vih&apos;s
          </motion.p>

          <motion.h1
            className="vihs-hero-title"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            Criação digital sob medida.
          </motion.h1>

          <motion.p
            className="vihs-hero-lead"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            Sistemas, websites, bots Discord, design e infraestrutura — entregues com rigor
            de estúdio profissional.
          </motion.p>

          <motion.div
            className="vihs-hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              className="vihs-btn vihs-btn-primary"
              onClick={openProjectRequest}
            >
              Solicitar projeto
              <FaArrowRight aria-hidden="true" />
            </button>
            <a href="#ecossistema" className="vihs-btn vihs-btn-ghost">
              Conhecer o ecossistema
            </a>
          </motion.div>
        </div>
      </section>

      <section id="servicos" className="vihs-section vihs-services">
        <div className="vihs-shell">
          <motion.div
            className="vihs-section-head"
            {...fadeUp}
            whileInView="animate"
            initial="initial"
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55 }}
          >
            <p className="vihs-eyebrow">Serviços</p>
            <h2 className="vihs-section-title">O que entregamos.</h2>
            <p className="vihs-section-lead">
              Um portfólio de criação focado em resultado — da ideia à operação.
            </p>
          </motion.div>

          <ul className="vihs-service-list">
            {services.map((service, index) => (
              <motion.li
                key={service.name}
                className="vihs-service-row"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
              >
                <span className="vihs-service-index">{String(index + 1).padStart(2, '0')}</span>
                <div className="vihs-service-copy">
                  <h3>{service.name}</h3>
                  <p>{service.detail}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      <section id="ecossistema" className="vihs-section vihs-ecosystem">
        <div className="vihs-shell">
          <motion.div
            className="vihs-section-head"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55 }}
          >
            <p className="vihs-eyebrow">Ecossistema</p>
            <h2 className="vihs-section-title">Três frentes. Uma marca.</h2>
            <p className="vihs-section-lead">
              A Vih&apos;s conecta criação comercial e produtos digitais em um só universo.
            </p>
          </motion.div>

          <div className="vihs-eco-grid">
            {ecosystem.map((item, index) => {
              const content = (
                <>
                  <div className="vihs-eco-meta">
                    <span className="vihs-eco-role">{item.role}</span>
                    <h3>{item.name}</h3>
                    <p>{item.description}</p>
                  </div>
                  {item.cta && (
                    <span className="vihs-eco-cta">
                      {item.cta}
                      <FaArrowRight aria-hidden="true" />
                    </span>
                  )}
                </>
              );

              const className = `vihs-eco-item${item.primary ? ' is-primary' : ''}`;

              return item.href ? (
                <motion.a
                  key={item.name}
                  href={item.href}
                  className={className}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  {content}
                </motion.a>
              ) : (
                <motion.div
                  key={item.name}
                  className={className}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  {content}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="sobre" className="vihs-section vihs-about">
        <div className="vihs-shell vihs-about-layout">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55 }}
          >
            <p className="vihs-eyebrow">Sobre</p>
            <h2 className="vihs-section-title">A marca-mãe do ecossistema.</h2>
          </motion.div>
          <motion.p
            className="vihs-about-text"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55, delay: 0.08 }}
          >
            A Vih&apos;s é a central que apresenta e organiza o Studio, o Bot e o Dev Hub.
            Existimos para oferecer criação profissional — com clareza de processo, qualidade
            de entrega e uma identidade única em cada projeto.
          </motion.p>
        </div>
      </section>

      <section className="vihs-cta-band">
        <div className="vihs-shell vihs-cta-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.55 }}
          >
            <h2 className="vihs-cta-title">Pronto para construir o próximo projeto?</h2>
            <p className="vihs-cta-lead">
              Fale conosco e transforme a demanda em entrega concreta.
            </p>
          </motion.div>
          <button
            type="button"
            className="vihs-btn vihs-btn-primary"
            onClick={openProjectRequest}
          >
            Solicitar projeto
            <FaArrowRight aria-hidden="true" />
          </button>
        </div>
      </section>

      <footer className="vihs-footer">
        <div className="vihs-shell vihs-footer-grid">
          <div className="vihs-footer-brand">
            <span className="vihs-footer-logo">
              <VihsMark />
              Vih&apos;s
            </span>
            <p>Criação digital sob medida.</p>
          </div>
          <div className="vihs-footer-cols">
            <div>
              <h4>Navegar</h4>
              <a href="#servicos">Serviços</a>
              <a href="#ecossistema">Ecossistema</a>
              <a href="#sobre">Sobre</a>
            </div>
            <div>
              <h4>Projetos</h4>
              <a href="/studio">Studio</a>
              <a href="/dev-hub">Dev Hub</a>
              <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
                Bot
              </a>
            </div>
            <div>
              <h4>Contato</h4>
              <a href="https://instagram.com/vihsoficial" target="_blank" rel="noopener noreferrer">
                Instagram
              </a>
              <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
                Discord
              </a>
            </div>
          </div>
        </div>
        <div className="vihs-shell vihs-footer-bottom">
          <p>© {new Date().getFullYear()} Vih&apos;s. Todos os direitos reservados.</p>
          <Link to="/termos">Termos e Políticas</Link>
        </div>
      </footer>
    </div>
  );
}
